"use client";
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { memberService, departmentService, canManageMembers, type Member, type Department } from '@/lib/supabase-service';

const PIPELINE_STAGES = [
  { key: 'visitor', label: 'First Visit', color: 'text-gold border-gold/40 bg-gold/10', step: 1 },
  { key: 'membership_class', label: 'Membership Class', color: 'text-blue-400 border-blue-400/40 bg-blue-400/10', step: 2 },
  { key: 'full_member', label: 'Full Member', color: 'text-green-400 border-green-400/40 bg-green-400/10', step: 3 },
] as const;

const DEPARTMENTS_LIST = ['Choir', 'Deacons', 'Youth Ministry', 'Prayer Team', 'Ushers', 'Media Team', 'Hospitality', 'Missions'];

export default function PipelinePage() {
  const router = useRouter();
  const { user, profile, loading: authLoading } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [activeStage, setActiveStage] = useState<Member['pipelineStage'] | 'all'>('all');
  const [search, setSearch] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [assignDept, setAssignDept] = useState('');
  const [assignStage, setAssignStage] = useState<Member['pipelineStage']>('visitor');

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/login');
      return;
    }
    if (!authLoading && user) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = memberService.subscribeToChanges((updatedMembers) => {
      setMembers(updatedMembers);
    });
    return unsubscribe;
  }, [user]);

  async function loadData() {
    setDataLoading(true);
    try {
      const [membersData, deptsData] = await Promise.all([
        memberService.getAll(),
        departmentService.getAll(),
      ]);
      setMembers(membersData);
      setDepartments(deptsData);
    } finally {
      setDataLoading(false);
    }
  }

  const canEdit = profile ? canManageMembers(profile.role) : false;

  const filtered = members.filter(m => {
    const matchStage = activeStage === 'all' || m.pipelineStage === activeStage;
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase());
    return matchStage && matchSearch;
  });

  async function advanceStage(member: Member) {
    const stages: Member['pipelineStage'][] = ['visitor', 'membership_class', 'full_member'];
    const idx = stages.indexOf(member.pipelineStage);
    if (idx < stages.length - 1) {
      const newStage = stages[idx + 1];
      const newStatus = newStage === 'full_member' ? 'Active' as const : member.status;
      await memberService.update(member.id, { pipelineStage: newStage, status: newStatus });
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, pipelineStage: newStage, status: newStatus } : m));
    }
  }

  function openAssign(member: Member) {
    setSelectedMember(member);
    setAssignDept(member.department || '');
    setAssignStage(member.pipelineStage);
    setShowAssignModal(true);
  }

  async function saveAssignment() {
    if (!selectedMember) return;
    const newStatus = assignStage === 'full_member' ? 'Active' as const : selectedMember.status;
    await memberService.update(selectedMember.id, { department: assignDept, pipelineStage: assignStage, status: newStatus });
    setMembers(prev => prev.map(m => m.id === selectedMember.id ? { ...m, department: assignDept, pipelineStage: assignStage, status: newStatus } : m));
    setShowAssignModal(false);
  }

  const stageCounts = {
    visitor: members.filter(m => m.pipelineStage === 'visitor').length,
    membership_class: members.filter(m => m.pipelineStage === 'membership_class').length,
    full_member: members.filter(m => m.pipelineStage === 'full_member').length,
  };

  if (authLoading) return (
    <div className="min-h-screen bg-sanctuary flex items-center justify-center">
      <div className="noise-overlay" aria-hidden="true" />
      <p className="font-mono text-sm text-silver-dim relative z-10">Loading…</p>
    </div>
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sanctuary">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="mb-10">
          <span className="section-label block mb-2">Compassion Check-in // Intake Pipeline</span>
          <h1 className="text-silver font-black text-3xl tracking-tight uppercase">Member Pipeline</h1>
          <p className="text-silver-dim text-sm mt-1 font-light">Track every person from first visit through full membership and department assignment.</p>
        </div>

        {/* Pipeline stage stats */}
        <div className="grid grid-cols-3 gap-px bg-border-strong border border-border-strong mb-8">
          {PIPELINE_STAGES.map(stage => (
            <button
              key={stage.key}
              onClick={() => setActiveStage(activeStage === stage.key ? 'all' : stage.key)}
              className={`bento-cell px-6 py-5 text-left transition-colors hover:bg-vestment ${activeStage === stage.key ? 'bg-vestment' : ''}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border ${stage.color}`}>Step {stage.step}</span>
              </div>
              <div className="stat-number text-3xl">{stageCounts[stage.key]}</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-silver-dim opacity-60 mt-1">{stage.label}</div>
            </button>
          ))}
        </div>

        {/* Pipeline flow diagram */}
        <div className="border border-border-strong bg-vestment px-6 py-4 mb-8 flex items-center gap-3 overflow-x-auto">
          {PIPELINE_STAGES.map((stage, i) => (
            <React.Fragment key={stage.key}>
              <div className={`flex items-center gap-2 px-4 py-2 border flex-shrink-0 ${stage.color}`}>
                <span className="font-mono text-[10px] tracking-wider uppercase">{stage.label}</span>
              </div>
              {i < PIPELINE_STAGES.length - 1 && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-silver-dim opacity-40 flex-shrink-0">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              )}
            </React.Fragment>
          ))}
          <div className="ml-auto flex-shrink-0">
            <span className="font-mono text-[9px] tracking-widest uppercase text-silver-dim opacity-40">→ Department Assignment</span>
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors flex-1"
          />
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setActiveStage('all')} className={`font-mono text-[10px] tracking-wider uppercase px-4 py-2 border transition-colors ${activeStage === 'all' ? 'border-gold text-gold' : 'border-border-strong text-silver-dim hover:border-gold hover:text-gold'}`}>All</button>
            {PIPELINE_STAGES.map(s => (
              <button key={s.key} onClick={() => setActiveStage(s.key)} className={`font-mono text-[10px] tracking-wider uppercase px-4 py-2 border transition-colors ${activeStage === s.key ? 'border-gold text-gold' : 'border-border-strong text-silver-dim hover:border-gold hover:text-gold'}`}>{s.label}</button>
            ))}
          </div>
        </div>

        {/* Member list */}
        <div className="border border-border-strong space-y-px">
          {dataLoading && (
            <div className="bento-cell px-6 py-12 text-center">
              <p className="font-mono text-sm text-silver-dim opacity-50">Loading pipeline…</p>
            </div>
          )}
          {!dataLoading && filtered.length === 0 && (
            <div className="bento-cell px-6 py-12 text-center">
              <p className="font-mono text-sm text-silver-dim opacity-50">No members found.</p>
            </div>
          )}
          {!dataLoading && filtered.map(member => {
            const stage = PIPELINE_STAGES.find(s => s.key === member.pipelineStage)!;
            const isLastStage = member.pipelineStage === 'full_member';
            return (
              <div key={member.id} className="bento-cell px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-silver font-semibold text-sm">{member.name}</span>
                    <span className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border ${stage.color}`}>{stage.label}</span>
                    {member.department && (
                      <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border border-silver-dim/30 text-silver-dim">{member.department}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                    <span className="font-mono text-xs text-silver-dim opacity-70">{member.email}</span>
                    <span className="font-mono text-xs text-silver-dim opacity-50">Last visit: {member.lastVisit}</span>
                    <span className="font-mono text-xs text-silver-dim opacity-50">Joined: {member.joinDate}</span>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!isLastStage && (
                      <button
                        onClick={() => advanceStage(member)}
                        className="font-mono text-[10px] tracking-wider uppercase px-4 py-2 border border-gold/40 text-gold hover:bg-gold/10 transition-colors"
                      >
                        Advance →
                      </button>
                    )}
                    <button
                      onClick={() => openAssign(member)}
                      className="font-mono text-[10px] tracking-wider uppercase px-4 py-2 border border-border-strong text-silver-dim hover:border-gold hover:text-gold transition-colors"
                    >
                      Assign
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Department Rosters */}
        <div className="mt-12">
          <h2 className="text-silver font-black text-xl tracking-tight uppercase mb-6">Department Rosters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border-strong border border-border-strong">
            {departments.map(dept => {
              const deptMembers = members.filter(m => m.department === dept.name);
              return (
                <div key={dept.id} className="bento-cell p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-silver font-semibold text-sm">{dept.name}</h3>
                      <p className="font-mono text-[10px] text-silver-dim opacity-60 mt-0.5">Leader: {dept.leader}</p>
                    </div>
                    <span className="font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border border-border-strong text-silver-dim">{deptMembers.length} members</span>
                  </div>
                  {deptMembers.length === 0 ? (
                    <p className="font-mono text-[10px] text-silver-dim opacity-40">No members assigned</p>
                  ) : (
                    <ul className="space-y-1">
                      {deptMembers.map(m => (
                        <li key={m.id} className="font-mono text-[11px] text-silver-dim flex items-center gap-2">
                          <span className="w-1 h-1 bg-gold rounded-full flex-shrink-0" />
                          {m.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />

      {/* Assign Modal */}
      {showAssignModal && selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-sanctuary/80 backdrop-blur-sm px-4">
          <div className="bg-vestment border border-border-strong w-full max-w-md p-8">
            <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-1">Assign Member</h2>
            <p className="text-silver font-semibold text-sm mb-6">{selectedMember.name}</p>

            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Pipeline Stage</label>
                <select
                  value={assignStage}
                  onChange={e => setAssignStage(e.target.value as Member['pipelineStage'])}
                  className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors"
                >
                  {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Department</label>
                <select
                  value={assignDept}
                  onChange={e => setAssignDept(e.target.value)}
                  className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors"
                >
                  <option value="">No Department</option>
                  {DEPARTMENTS_LIST.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={saveAssignment} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest flex-1">Save</button>
              <button onClick={() => setShowAssignModal(false)} className="btn-ghost text-[11px] px-6 py-3 font-mono tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
